/**
 * SubstitutionPopover — discrete "💡 Save ₵X" badge that opens a popover
 * listing AI-ranked therapeutic equivalents currently in stock for the
 * given cart item. One-click swap replaces the cart line.
 *
 * Usage:
 *   <SubstitutionPopover
 *     item={cartItem}
 *     onSwap={(newProduct, savings) => replaceLine(...)}
 *     patientId={selectedPatientId}
 *     saleId={null}
 *   />
 *
 * Self-fetches its own state on first open. Renders nothing if there are
 * no cheaper equivalents, so it stays out of the way for OTC / unbranded
 * items where substitution adds no value.
 */
import { useState, useEffect, useCallback } from 'react';
import { Lightbulb, ArrowRight, Loader2, X, ShieldAlert, Package } from 'lucide-react';
import {
  Popover, PopoverTrigger, PopoverContent,
} from './ui/popover';
import { Button } from './ui/button';
import { toast } from 'sonner';
import api from '../lib/api';
import { fmtCurrency } from '../lib/localeUtils';

export default function SubstitutionPopover({ item, onSwap, patientId, saleId }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null); // {source, substitutes, policy, reason, best_savings_per_unit}
  const [hasFetched, setHasFetched] = useState(false);
  const [bestSavings, setBestSavings] = useState(null);

  // Probe once on mount so we know whether to show the chip at all.
  // The probe is the same endpoint as the popover content — caching the
  // result avoids a second roundtrip.
  const fetchSubs = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get(
        `/medications/substitutes/${item.product_id}`,
        { params: { limit: 6 } },
      );
      setData(res);
      setBestSavings(res?.best_savings_per_unit || 0);
      setHasFetched(true);
    } catch (e) {
      // Quietly skip — substitution is best-effort.
      setData({ substitutes: [], reason: 'Failed to load' });
      setBestSavings(0);
    } finally {
      setLoading(false);
    }
  }, [item.product_id]);

  useEffect(() => {
    if (!hasFetched) fetchSubs();
  }, [fetchSubs, hasFetched]);

  const accept = async (target) => {
    try {
      await api.post(`/medications/substitutes/${item.product_id}/accept`, {
        target_product_id: target.product_id,
        quantity: item.quantity || 1,
        sale_id: saleId,
        patient_id: patientId,
      });
      toast.success(
        `Patient saves ${fmtCurrency(target.savings_per_unit * (item.quantity || 1))} this visit`,
        { description: `Swapped to ${target.name}` },
      );
      onSwap?.(target, target.savings_per_unit);
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to record substitution');
    }
  };

  // Hide the chip entirely if no real savings opportunity exists.
  if (!hasFetched && !loading) return null;
  if (hasFetched && (!bestSavings || bestSavings <= 0)) return null;

  const totalSavings = (bestSavings || 0) * (item.quantity || 1);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`substitute-chip-${item.product_id}`}
          className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-700 hover:bg-emerald-500/25 border border-emerald-500/30 transition-colors"
          title="Cheaper generic alternatives available"
        >
          <Lightbulb className="h-2.5 w-2.5" />
          Save {fmtCurrency(totalSavings)}
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="start"
        className="w-[420px] p-0"
        data-testid="substitution-popover"
      >
        <div className="p-3 border-b bg-emerald-50/60">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h4 className="text-sm font-semibold text-emerald-900 flex items-center gap-1.5">
                <Lightbulb className="h-4 w-4" />
                Cheaper alternatives
              </h4>
              <p className="text-[11px] text-emerald-800/80 mt-0.5">
                Same generic ({data?.source?.generic_name || 'INN'}) — currently in stock
              </p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 -mt-1 -mr-1"
              onClick={() => setOpen(false)}
              data-testid="substitution-close"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-10 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin mr-2" /> Finding equivalents…
          </div>
        ) : (data?.substitutes || []).length === 0 ? (
          <div className="flex flex-col items-center py-8 px-4 text-center text-muted-foreground">
            <ShieldAlert className="h-6 w-6 mb-2 opacity-40" />
            <p className="text-xs">{data?.reason || 'No equivalents currently in stock.'}</p>
          </div>
        ) : (
          <ul className="divide-y divide-border/60 max-h-[320px] overflow-y-auto">
            {/* Source for comparison */}
            <li className="px-3 py-2 bg-muted/30">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Currently dispensing</p>
                  <p className="text-sm font-medium truncate">{data?.source?.name}</p>
                  <p className="text-[10px] text-muted-foreground">{data?.source?.manufacturer || 'Brand name'}</p>
                </div>
                <span className="text-sm font-bold tabular-nums">{fmtCurrency(data?.source?.unit_price || 0)}</span>
              </div>
            </li>
            {(data.substitutes || []).map((s) => (
              <li
                key={s.product_id}
                className="px-3 py-2.5 hover:bg-emerald-50/40 transition-colors group"
                data-testid={`substitute-row-${s.product_id}`}
              >
                <div className="flex items-start justify-between gap-2 mb-1.5">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{s.name}</p>
                    <div className="flex items-center gap-2 text-[10px] text-muted-foreground mt-0.5">
                      <span>{s.manufacturer || 'Generic'}</span>
                      <span className="flex items-center gap-0.5"><Package className="h-2.5 w-2.5" /> {s.valid_stock} in stock</span>
                      {s.expiry && <span>· exp {s.expiry}</span>}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-bold tabular-nums">{fmtCurrency(s.unit_price)}</p>
                    {s.savings_per_unit > 0 && (
                      <p className="text-[10px] font-semibold text-emerald-700">
                        −{s.savings_pct.toFixed(0)}% · save {fmtCurrency(s.savings_per_unit)}
                      </p>
                    )}
                  </div>
                </div>
                <Button
                  size="sm"
                  className="w-full h-7 text-xs gap-1 bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => accept(s)}
                  data-testid={`substitute-accept-${s.product_id}`}
                >
                  <ArrowRight className="h-3 w-3" />
                  Swap to this generic
                </Button>
              </li>
            ))}
          </ul>
        )}

        <div className="px-3 py-2 text-[10px] text-muted-foreground border-t bg-muted/20">
          Substitution policy: <b className="text-foreground">{data?.policy?.policy?.replace('_', ' ') || 'pharmacist approval'}</b>
          {data?.source?.generic_name && (
            <> · INN <b className="text-foreground">{data.source.generic_name}</b></>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
